import * as Sentry from '@sentry/react'
import {
  createApiClient,
  createApiQueryClient,
  createAuthMiddleware,
  createTokenRefreshMiddleware,
  type MainApiPaths,
  type Middleware,
} from '@mdhp/api-client'
import { useAuthStore } from '../store/authStore'
import { buildLoginUrl } from '../utils/redirectTo'

/**
 * Sentry error reporting middleware — captures non-401 errors.
 */
const sentryMiddleware: Middleware = {
  onResponse: async ({ response, request }) => {
    if (!response.ok && response.status !== 401) {
      Sentry.captureMessage(`API error: ${response.status} ${response.statusText}`, {
        level: 'error',
        contexts: {
          api: {
            client: 'mainApiClient',
            url: request.url,
            method: request.method,
            status: response.status,
            statusText: response.statusText,
          },
        },
        tags: {
          api_client: 'authenticated',
          http_status: response.status.toString(),
        },
      })
    }
    return response
  },
}

const fetchClient = createApiClient<MainApiPaths>({
  baseUrl: import.meta.env.VITE_API_URL,
  middleware: [
    createAuthMiddleware((request) => {
      if (request.headers.get('Authorization')) {
        return null
      }

      const { token, tokenType } = useAuthStore.getState()

      if (token) {
        return { accessToken: token, tokenType: tokenType ?? 'bearer' }
      }
      return null
    }),
    createTokenRefreshMiddleware(async () => {
      const { refreshToken, setToken, setRefreshToken, clearToken } = useAuthStore.getState()

      if (!refreshToken) {
        clearToken()
        window.location.href = buildLoginUrl()
        return null
      }

      try {
        // Use a raw fetch for token refresh to avoid circular middleware
        const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/token/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        })

        if (!response.ok) {
          throw new Error(`Refresh failed: ${response.status}`)
        }

        const data = await response.json()

        setToken(data.access_token, data.token_type)
        if (data.refresh_token) {
          setRefreshToken(data.refresh_token)
        }

        return { access_token: data.access_token, token_type: data.token_type }
      } catch {
        clearToken()
        window.location.href = buildLoginUrl()
        return null
      }
    }),
    sentryMiddleware,
  ],
})

/**
 * Unauthenticated fetch client — for public endpoints that don't require auth.
 *
 * @example
 * ```ts
 * const { data } = await publicFetchClient.POST('/patients/{patient_id}/catalog/allergies', {
 *   body: payload,
 *   headers: { Authorization: `bearer ${token}` },
 * })
 * ```
 */
export const publicFetchClient = createApiClient<MainApiPaths>({
  baseUrl: import.meta.env.VITE_API_URL,
})

/**
 * Authenticated fetch client — use for non-React contexts.
 */
export const mainApiFetchClient = fetchClient

/**
 * Main API query client — provides typed React Query hooks.
 *
 * @example
 * ```ts
 * const { data } = mainApiClient.useQuery('get', '/patients/{patient_id}', {
 *   params: { path: { patient_id: '123' } },
 * })
 * ```
 */
export const mainApiClient = createApiQueryClient(fetchClient)
